import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_ROUND_NAME, defaultRoundName, type JeopardySharedRound } from '@gsf/games';

/** How much of the visible strip one arrow press moves. */
const SCROLL_FRACTION = 0.8;

/**
 * The rounds as one button each, not a dropdown: a live operator should see where the
 * game is and can go. Scrolls, with arrows, once the buttons stop fitting.
 */
export function RoundTabs({
  rounds,
  round,
  onPick,
  onAddRound,
  onRename,
  className = '',
}: {
  /** Every round this game can move between, ascending — `shared.rounds`. */
  rounds: readonly JeopardySharedRound[];
  round: number;
  onPick: (round: number) => void;
  /** Host only: start a round after the last one. Omit for a run-only strip. */
  onAddRound?: () => void;
  /** Host only: rename the round the game is on. Empty puts "Round N" back. */
  onRename?: (round: number, name: string) => void;
  className?: string;
}) {
  // A callback ref rather than useRef: the strip's width comes from its parent,
  // so there's nothing honest to measure until it's actually in the tree.
  const [track, setTrack] = useState<HTMLDivElement | null>(null);
  const [ends, setEnds] = useState({ left: false, right: false });
  const [renaming, setRenaming] = useState<string | null>(null);
  const renameBox = useRef<HTMLInputElement>(null);

  const list = rounds.length > 0 ? rounds : [{ round, name: defaultRoundName(round) }];
  const currentName = list.find((r) => r.round === round)?.name ?? defaultRoundName(round);
  // Re-measuring has to be driven by the rounds themselves, not the array
  // identity — a fresh state snapshot arrives on every broadcast.
  const key = list.map((r) => `${r.round}:${r.name}`).join(',');

  const measure = useCallback(() => {
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    // A pixel of slack: sub-pixel layout widths leave a fractional remainder
    // that isn't clipping anything, and would show a permanent dead arrow.
    setEnds({ left: track.scrollLeft > 1, right: track.scrollLeft < max - 1 });
  }, [track]);

  useEffect(() => {
    if (!track) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    track.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      track.removeEventListener('scroll', measure);
    };
  }, [track, measure, key]);

  // Keep the round you're on in view. It can move without anyone touching this
  // strip — the other screen changes rounds too — and on a long list the new
  // one may be sat off the end.
  useEffect(() => {
    if (!track) return;
    const current = track.querySelector<HTMLElement>('[data-current="true"]');
    if (!current) return;
    // scrollLeft rather than scrollIntoView, which would scroll every ancestor
    // to suit itself and lurch the whole TV.
    track.scrollLeft = current.offsetLeft - (track.clientWidth - current.offsetWidth) / 2;
  }, [track, round, key]);

  // Changing round mid-rename would commit the typing to the wrong one.
  useEffect(() => setRenaming(null), [round]);

  function nudge(direction: -1 | 1) {
    track?.scrollBy({
      left: direction * track.clientWidth * SCROLL_FRACTION,
      behavior: 'smooth',
    });
  }

  function startRename() {
    setRenaming(currentName);
    // The input replaces the current tab, which is a render away.
    requestAnimationFrame(() => renameBox.current?.select());
  }

  function commitRename() {
    if (renaming === null) return;
    // Typing the default back is the same as never having renamed it, and the
    // server reads an empty name as "clear it" — so both land on "Round N".
    if (renaming.trim() !== currentName) {
      onRename?.(round, renaming.trim() === defaultRoundName(round) ? '' : renaming);
    }
    setRenaming(null);
  }

  /**
   * Clicking leaves a button focused, and a focused button eats the next Space
   * — the host's reveal key on both screens.
   */
  const unfocus = (event: { currentTarget: HTMLElement }) => event.currentTarget.blur();

  // Both arrows come and go together, the unusable one greyed: toggling one at a time
  // changes the track's width mid-scroll and flickers.
  const scrollable = ends.left || ends.right;

  return (
    <div
      className={`round-tabs${scrollable ? ' is-scrollable' : ''}${className ? ` ${className}` : ''}`}
      role="group"
      aria-label="Round"
    >
      {scrollable ? (
        <button
          type="button"
          className="round-arrow"
          aria-label="Scroll to earlier rounds"
          disabled={!ends.left}
          onClick={(e) => {
            unfocus(e);
            nudge(-1);
          }}
        >
          ‹
        </button>
      ) : null}

      <div className="round-tabs-track" ref={setTrack}>
        {list.map(({ round: r, name }) => {
          const current = r === round;

          // The name is edited in the tab's own place, so the strip doesn't
          // reflow and it's obvious which round is being renamed.
          if (current && renaming !== null) {
            return (
              <input
                key={r}
                ref={renameBox}
                className="round-tab is-current round-rename"
                data-current="true"
                aria-label={`Rename ${name}`}
                value={renaming}
                maxLength={MAX_ROUND_NAME}
                size={Math.max(renaming.length, 6)}
                autoFocus
                onChange={(e) => setRenaming(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  // Stop Space/Y/N reaching the host's flow shortcuts.
                  e.stopPropagation();
                  if (e.key === 'Enter') e.currentTarget.blur();
                  else if (e.key === 'Escape') setRenaming(null);
                }}
              />
            );
          }

          return (
            <button
              key={r}
              type="button"
              className={`round-tab${current ? ' is-current' : ''}`}
              data-current={current ? 'true' : undefined}
              aria-current={current ? 'true' : undefined}
              disabled={current}
              title={current ? `Currently on ${name}` : `Jump to ${name}`}
              onClick={(e) => {
                unfocus(e);
                onPick(r);
              }}
            >
              {name}
            </button>
          );
        })}
      </div>

      {scrollable ? (
        <button
          type="button"
          className="round-arrow"
          aria-label="Scroll to later rounds"
          disabled={!ends.right}
          onClick={(e) => {
            unfocus(e);
            nudge(1);
          }}
        >
          ›
        </button>
      ) : null}

      {/* Outside the track, so they stay put where the hand expects them
          however far the rounds have been scrolled. */}
      {onRename ? (
        <button
          type="button"
          className="round-edit"
          aria-label={`Rename ${currentName}`}
          title={`Rename ${currentName}`}
          disabled={renaming !== null}
          onClick={(e) => {
            unfocus(e);
            startRename();
          }}
        >
          ✎
        </button>
      ) : null}

      {onAddRound ? (
        <button
          type="button"
          className="round-add"
          aria-label="Add a round"
          title={`Start ${defaultRoundName(
            Math.max(...list.map((r) => r.round)) + 1,
          )} — a blank board to fill`}
          onClick={(e) => {
            unfocus(e);
            onAddRound();
          }}
        >
          +
        </button>
      ) : null}
    </div>
  );
}
